import React from 'react';
import { CarComparisonData } from '../../types/carComparison';
import { calculateResults, calculateComparisonResults, calculateFinancePrincipalInterest, calculateLeasePrincipalInterest } from '../../utils/carCalculations';
import { TylerState } from '../../utils/TylerState';
import { useTylerStateUpdate } from '../../utils/TylerState';

interface ComparisonResultsProps {
  data: CarComparisonData;
  comparisonData: CarComparisonData;
  formatCurrency: (value: number) => string;
}

const ComparisonResults: React.FC<ComparisonResultsProps> = ({
  data,
  comparisonData,
  formatCurrency
}) => {
  useTylerStateUpdate();

  const carAFinance = calculateFinancePrincipalInterest(data);
  const carALease = calculateLeasePrincipalInterest(data);
  const carBFinance = calculateFinancePrincipalInterest(comparisonData);
  const carBLease = calculateLeasePrincipalInterest(comparisonData);
  const carATotalCost = Math.abs(calculateResults(data).buy.finalBalance);
  const carBTotalCost = Math.abs(calculateResults(comparisonData).buy.finalBalance);
  const winner = carATotalCost < carBTotalCost ? "A" : (carATotalCost > carBTotalCost ? "B" : "Tie");
  const savings = Math.abs(carATotalCost - carBTotalCost);

  // Get TylerState flags for conditional rendering
  const includeFinance = TylerState.car.includeFinanceScenario;
  const includeLease = TylerState.car.includeLeaseScenario;

  const comparisonJSX = (
 
    <div className="comparison-summary" style={{
      backgroundColor: '#f0fdf4',
      border: '2px solid #22c55e',
      borderRadius: '8px',
      padding: '1rem',
      marginBottom: '1rem',
      textAlign: 'center'
    }}>
      <h4 style={{ color: '#16a34a', margin: '0 0 0.5rem 0' }}>🏆 
        {winner === "A" && (
          <>{data.scenarioName || "Car A"}</>
        )}
        {winner === "B" && (
          <>{comparisonData.scenarioName || "Car B"}</>
        )}
        &nbsp;is the winner!
      </h4>
      <b style={{ color: '#15803d' }}>{formatCurrency(savings)} cheaper overall!</b>
      <p className="comparison-note" style={{ fontSize: '0.875rem', color: '#166534', marginTop: '0.5rem', marginBottom: 0 }}>
        This comparison shows the total cost of ownership over {data.ownershipTimeframe} years,
        including purchase price, fuel, insurance, maintenance, and opportunity costs.
      </p>
    </div>
  );
  const TieJSX = (
    <div className="comparison-summary" style={{
      backgroundColor: '#f0fdf4',
      border: '2px solid #22c55e',
      borderRadius: '8px',
      padding: '1rem',
      marginBottom: '1rem',
      textAlign: 'center'
    }}>
      <h4> It's a Tie! These cars have the same cost!</h4>
    </div>
  );

  return (
    <div className="comparison-results">
      <br/>
      <h2>Total Cost Comparison </h2>
      {winner === "A" || winner === "B" && comparisonJSX}
      {winner === "Tie" && TieJSX}
      <div className="summary-cards-container" style={{ marginBottom: '2rem' }}>
        {/* Car A Summary Card */}
        <div className="summary-card comparison-mode">
          <div className="summary-card-header">
            <h3>{data.scenarioName || "Car A"}</h3>
            <span className="summary-card-icon">🚗</span>
          </div>
          <div className="summary-metrics">
            <div className="metric">
              <span className="metric-label">Total Cost ({data.ownershipTimeframe} years)</span>
              <span className="metric-value  primary">
                {formatCurrency(Math.abs(calculateResults(data).buy.finalBalance))}
              </span>
            </div>
                        <div className="metric">
              <span className="metric-label">Purchase Price</span>
              <span className="metric-value">
                {formatCurrency(data.totalCost)}
              </span>
            </div>

            <div className="metric">
              <span className="metric-label">Fuel Efficiency</span>
              <span className="metric-value">
                {data.fuelEfficiency} {data.region === 'Canada' ? 'L/100km' : 'MPG'}
              </span>
            </div>

            <div className="metric">
              <span className="metric-label">Annual Mileage</span>
              <span className="metric-value muted">
                {data.annualMileage.toLocaleString()} {data.region === 'Canada' ? 'km' : 'miles'}
              </span>
            </div>

            <div className="metric">
              <span className="metric-label">Insurance</span>
              <span className="metric-value muted">
                {formatCurrency(data.annualInsurance)}/year
              </span>
            </div>

            <div className="metric">
              <span className="metric-label">Maintenance</span>
              <span className="metric-value muted">
                {formatCurrency(data.maintenanceCost)}/year
              </span>
            </div>
          </div>

          {/* Finance Scenario - Conditional */}
          {includeFinance && (
            <div className="summary-metrics" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '1rem' }}>
              <div className="metric-label" style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Finance Scenario</div>
              <div className="metric">
                <span className="metric-label">Total Payments</span>
                <span className="metric-value">{formatCurrency(carAFinance.totalPayments)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Principal</span>
                <span className="metric-value muted">{formatCurrency(carAFinance.principal)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Interest</span>
                <span className="metric-value muted">{formatCurrency(carAFinance.interest)}</span>
              </div>
            </div>
          )}

          {/* Lease Scenario - Conditional */}
          {includeLease && (
            <div className="summary-metrics" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '1rem' }}>
              <div className="metric-label" style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Lease Scenario</div>
              <div className="metric">
                <span className="metric-label">Total Payments</span>
                <span className="metric-value">{formatCurrency(carALease.totalPayments)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Principal (Depreciation)</span>
                <span className="metric-value muted">{formatCurrency(carALease.principal)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Interest</span>
                <span className="metric-value muted">{formatCurrency(carALease.interest)}</span>
              </div>
            </div>
          )}
        </div>

        <div className="vs-indicator">VS</div>

        {/* Car B Summary Card */}
        <div className="summary-card comparison-mode">
          <div className="summary-card-header">
            <h3>{comparisonData.scenarioName || "Car B"}</h3>
            <span className="summary-card-icon">🚙</span>
          </div>
          <div className="summary-metrics">


            <div className="metric">
              <span className="metric-label">Total Cost ({comparisonData.ownershipTimeframe} years)</span>
              <span className="metric-value primary">
                {formatCurrency(Math.abs(calculateComparisonResults(comparisonData).buy.finalBalance))}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Purchase Price</span>
              <span className="metric-value">
                {formatCurrency(comparisonData.totalCost)}
              </span>
            </div>
            <div className="metric">
              <span className="metric-label">Fuel Efficiency</span>
              <span className="metric-value">
                {comparisonData.fuelEfficiency} {comparisonData.region === 'Canada' ? 'L/100km' : 'MPG'}
              </span>
            </div>

            <div className="metric">
              <span className="metric-label">Annual Mileage</span>
              <span className="metric-value muted">
                {comparisonData.annualMileage.toLocaleString()} {comparisonData.region === 'Canada' ? 'km' : 'miles'}
              </span>
            </div>

            <div className="metric">
              <span className="metric-label">Insurance</span>
              <span className="metric-value muted">
                {formatCurrency(comparisonData.annualInsurance)}/year
              </span>
            </div>

            <div className="metric">
              <span className="metric-label">Maintenance</span>
              <span className="metric-value muted">
                {formatCurrency(comparisonData.maintenanceCost)}/year
              </span>
            </div>
          </div>

          {/* Finance Scenario - Conditional */}
          {includeFinance && (
            <div className="summary-metrics" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '1rem' }}>
              <div className="metric-label" style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Finance Scenario</div>
              <div className="metric">
                <span className="metric-label">Total Payments</span>
                <span className="metric-value">{formatCurrency(carBFinance.totalPayments)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Principal</span>
                <span className="metric-value muted">{formatCurrency(carBFinance.principal)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Interest</span>
                <span className="metric-value muted">{formatCurrency(carBFinance.interest)}</span>
              </div>
            </div>
          )}

          {/* Lease Scenario - Conditional */}
          {includeLease && (
            <div className="summary-metrics" style={{ borderTop: '1px solid #e5e7eb', paddingTop: '1rem', marginTop: '1rem' }}>
              <div className="metric-label" style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Lease Scenario</div>
              <div className="metric">
                <span className="metric-label">Total Payments</span>
                <span className="metric-value">{formatCurrency(carBLease.totalPayments)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Principal (Depreciation)</span>
                <span className="metric-value muted">{formatCurrency(carBLease.principal)}</span>
              </div>
              <div className="metric">
                <span className="metric-label">Interest</span>
                <span className="metric-value muted">{formatCurrency(carBLease.interest)}</span>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default ComparisonResults;